/**
 * @description Component-TransferHistory
 */
import './index.less';

import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ExternalLink } from '@/components/Link';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useInfiniteTransferHistory } from '@/features/graph/hooks';
import { TransferHistory as TransferHistoryType } from '@/features/graph/types';
import { useCurrentPairFromUrl } from '@/features/pair/hook';
import { useChainId, useEtherscanLink, useUserAddr } from '@/hooks/web3/useChain';
import { formatDate } from '@/utils/timeUtils';

import Empty from '@/components/Empty';
import MobileHistoryCard from '../MobileHistoryCard';
import MobileHistorySkeleton from '../MobileHistoryCard/Skeleton';

interface IPropTypes {
  isTradePage?: boolean;
}

const TransferHistory: FC<IPropTypes> = ({ isTradePage = false }) => {
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const { t } = useTranslation();
  const currentPair = useCurrentPairFromUrl(chainId);
  const pair = isTradePage ? currentPair : 'all';
  const {
    histories,
    queryState: { isLoading, isFetching },
  } = useInfiniteTransferHistory({ chainId, userAddr, pair });

  const loading = isLoading || isFetching;

  const getEtherscanLink = useEtherscanLink();

  const renderCard = (record: TransferHistoryType) => {
    return (
      <MobileHistoryCard key={record.id}>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.time')}</div>
          <div className="value">
            <ExternalLink href={getEtherscanLink(record.txHash, 'transaction')}>
              <div className="history-time-with-icon">
                <span>{formatDate(record.timestamp * 1000)}</span>
              </div>
            </ExternalLink>
          </div>
        </div>{' '}
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.direction')}</div>
          <div className="value">
            {record.typeString !== 'scatter' ? t('common.table.transferIn') : t('common.table.transferOut')}
          </div>
        </div>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.amount')}</div>
          <div className="value">
            {record?.quoteToken &&
              WrappedBigNumber.from(record.value).formatNumberWithTooltip({
                suffix: record?.quoteToken.symbol,
              })}
          </div>
        </div>
      </MobileHistoryCard>
    );
  };

  return (
    <>
      <div className="syn-mobile-history-transfer">
        {loading ? <MobileHistorySkeleton /> : histories.slice(0, 10).map(renderCard)}
      </div>
      {!loading && !!histories.length && <div className="syn-mobile-history-info">{t('mobile.history.tableInfo')}</div>}
      {!loading && !histories?.length && <Empty type="vertical" />}
    </>
  );
};

export default TransferHistory;
